import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { loginManager } from "@/services/security/login-manager";
import { userManager } from "@/services/security/user-manager";

// Source unique de vérité pour la configuration du composant <Vidle> dans App.vue,
// qui importe SESSION_DURATION_SECONDS/REMINDER_BEFORE_SECONDS pour ses props
// :duration et :reminders plutôt que de dupliquer ces valeurs.
// Fenêtre glissante d'inactivité de 30 min côté serveur (refresh token). Valeur cliente
// volontairement inférieure (5 min de marge) : à la limite exacte de 30 min, un
// refreshConnection()/revokeSession() déclenché par le timeout client (latence réseau,
// léger désync d'horloge) pourrait arriver après l'expiration serveur et provoquer une
// déconnexion forcée sur une session qui aurait dû être encore valide.
export const SESSION_DURATION_SECONDS = 25 * 60;
// Garder 5 min de préavis (un sixième de la fenêtre utile) était pertinent sur l'ancienne
// session d'1h, mais devient trop long ici. Réduit à 2 min pour laisser un délai de
// réaction confortable sans amputer une part disproportionnée des 25 min de session.
export const REMINDER_BEFORE_SECONDS = 2 * 60;

const SESSION_DURATION_MS = SESSION_DURATION_SECONDS * 1000;
const REMINDER_BEFORE_MS = REMINDER_BEFORE_SECONDS * 1000;
// Temps d'inactivité écoulé à partir duquel le rappel doit être affiché.
const REMINDER_AFTER_MS = SESSION_DURATION_MS - REMINDER_BEFORE_MS;

class UserTimeoutManager {
  logoutTimer = null;
  // Horodatage du début de la période d'inactivité courante.
  // Sert de filet de sécurité indépendant des setInterval/setTimeout, qui sont
  // fortement throttlés par Chrome sur les onglets en arrière-plan (intensive throttling).
  idleStart = Date.now();
  // Empêche l'affichage du rappel en double (voie <Vidle> @remind + voie checkOnVisible).
  reminded = false;

  // Appelé à chaque activité utilisateur détectée par <Vidle> (évènement @refresh).
  // Réinitialise l'horloge d'inactivité, à l'image du timer interne de <Vidle>.
  onActivity() {
    this.idleStart = Date.now();
    this.reminded = false;
  }

  async remind(isLogin = false) {
    if (isLogin || loginManager.getStayConnected()) {
      notificationManager.closeAlert();
      return;
    }
    // Empêche une seconde popup ou un second timer si un rappel est déjà en cours
    // (double déclenchement <Vidle> @remind + checkOnVisible).
    if (this.reminded) {
      return;
    }
    this.reminded = true;
    this.startLogoutTimer();
    const reminder = await notificationManager.showAlert(
      "warning",
      i18n.global.t("expired_session"),
      i18n.global.t("stay_connected_question"),
      false,
      false,
      i18n.global.t("stay_connected_confirm"),
      null,
      "#000",
      "",
      false,
      false,
    );

    if (reminder.isConfirmed) {
      this.clearTimerLogout();
      localStorage.removeItem("studea-tokenExpirationDate");
      await loginManager.refreshConnection();
      this.onActivity();
    }
  }

  logout(isLogin = false) {
    if (isLogin || loginManager.getStayConnected()) {
      notificationManager.closeAlert();
      return;
    }
    sessionStorage.setItem("studea-sessionExpired", 1);
    notificationManager.closeAlert();
    userManager.logout(true);
  }

  // Filet de sécurité (piste 1) : recalcule le temps d'inactivité réel à l'horloge
  // quand l'onglet redevient visible. Contourne le throttling / gel (freezing) des
  // timers de Chrome sur les onglets en arrière-plan, qui fait manquer au composant
  // <Vidle> l'égalité stricte du rappel (diff === 300) et retarde l'évènement idle.
  checkOnVisible(isLogin = false) {
    if (isLogin || loginManager.getStayConnected()) {
      return;
    }
    const idleFor = Date.now() - this.idleStart;
    if (idleFor >= SESSION_DURATION_MS) {
      // Ferme une éventuelle popup de rappel encore en attente de réponse, pour éviter
      // qu'elle ne se résolve après coup et ne relance une session déjà déconnectée.
      notificationManager.closeAlert();
      this.clearTimerLogout();
      this.logout(isLogin);
    } else if (idleFor >= REMINDER_AFTER_MS && !this.reminded) {
      this.remind(isLogin);
    }
  }

  startLogoutTimer() {
    this.clearTimerLogout();
    this.logoutTimer = setTimeout(() => this.logout(), REMINDER_BEFORE_MS);
  }

  clearTimerLogout() {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }
}

export const userTimeoutManager = new UserTimeoutManager();
